// ============================================================================
// Doc Order — E1–E5 email builders (pure). SINGLE SOURCE for both the actual
// sends (edge fn) and the modal previews (UI) — preview/send drift is
// structurally impossible. Copy is VERBATIM from the approved specs:
//   E1 A/B — specs/email-notifications.md (+ prototype NotificationEmailPreview)
//   E2     — prototype LoSendEmailPreview (portal link → production deep link)
//   E3/E4  — handoff-companion §A4 approved copy
// The HTML uses the Lendwise brand notice (../email-brand.ts, import-free): the
// copy, subjects and plain-text parts are unchanged; only the chrome is branded.
// ============================================================================

import {
  BRAND,
  STATUS_TONES,
  bulletList,
  button,
  callout,
  headerText,
  html,
  normalizeWebUrl,
  note,
  noticeEmail,
  panel,
  paragraph,
  signOff,
  type HtmlValue,
  type StatusChip,
} from '../email-brand.ts';

export interface EmailContent {
  subject: string;
  html: string;
  text: string;
}

/** The brand notice for a doc order: title, chip, "Loan · borrower" meta, the body, then the signature. */
function docOrderEmail(input: {
  title: string;
  status: StatusChip;
  loanNumber: string;
  borrowerName: string;
  blocks: readonly HtmlValue[];
  signName: string;
  signOrg: string;
}): string {
  const meta = `Loan ${input.loanNumber} · ${input.borrowerName}`;
  return noticeEmail({
    preheader: `${input.title} · ${meta}`,
    title: input.title,
    status: input.status,
    meta,
    blocks: [...input.blocks, signOff([`— ${input.signName}`, input.signOrg])],
  });
}

/** The attached PDF, shown as a bordered chip: filename, then what it is. */
function attachmentChip(filename: string, description: string) {
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:separate;margin:0 0 16px;"><tr><td style="border:1px solid ${BRAND.rule};border-radius:8px;background:${BRAND.paper};padding:8px 14px;font-family:Arial, Helvetica, sans-serif;font-size:14px;line-height:20px;color:${BRAND.ink};"><b>${filename}</b> &nbsp;<span style="color:${BRAND.muted};font-size:13px;">${description}</span></td></tr></table>`;
}

export interface OutstandingItem {
  tag: 'Deferred' | 'Pending' | 'Missing' | 'Action';
  label: string;
  reason?: string | null;
}

export interface InsuranceSnapshotRow {
  label: string;
  value: string;
}

export interface E1Input {
  loanNumber: string;
  borrowerName: string;
  processorName: string;
  docsRequestedBy: string; // formatted date
  outstanding: OutstandingItem[];
  sequence: number;
  resubmittedAt?: string | null; // formatted stamp, shown when sequence > 1
  deferred: boolean;
  deferReason?: string | null;
  /** §4 snapshot at submission (empty values already rendered as '—'). */
  insuranceSnapshot?: InsuranceSnapshotRow[];
  dicSnapshot?: InsuranceSnapshotRow[];
  hoaCoversWallsIn?: boolean;
}

/** Items that each lead with their own tag pill: rows without bullets. */
function taggedList(items: readonly HtmlValue[]) {
  const rows = items.map((item) => html`<tr><td style="padding:0 0 8px;font-family:Arial, Helvetica, sans-serif;font-size:14px;line-height:22px;color:${BRAND.bodyText};">${item}</td></tr>`);
  return html`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">${rows}</table>`;
}

/**
 * Snapshot rows: label column 40%, value 60%, both free to wrap (so a narrow
 * preview never squeezes the value to one character per line); an EMPTY value
 * shows as a bold em dash in the attention tone.
 */
function snapshotTable(rows: readonly InsuranceSnapshotRow[]) {
  const body = rows.map((r) => html`<tr><td valign="top" width="40%" style="width:40%;padding:4px 12px 4px 0;color:${BRAND.muted};border-bottom:1px solid ${BRAND.rule};">${r.label}</td><td valign="top" style="padding:4px 0;color:${BRAND.ink};border-bottom:1px solid ${BRAND.rule};overflow-wrap:anywhere;">${r.value ? r.value : html`<strong style="color:${STATUS_TONES.attention.text};">—</strong>`}</td></tr>`);
  return html`<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-size:13px;line-height:19px;">${body}</table>`;
}

/** E1 — Doc Order Submitted (standard A / deferred-insurance B). */
export function buildE1Submitted(input: E1Input): EmailContent {
  const subjBase = `Doc Order Submitted — Loan ${input.loanNumber} — ${input.borrowerName}`.trim();
  // headerText only turns control characters into spaces; the wording and the double space stay.
  const subject = headerText(input.deferred ? `${subjBase}  ⚠ Insurance Deferred` : subjBase);
  const visibleOutstanding = input.deferred
    ? input.outstanding.filter((o) => o.tag !== 'Deferred')
    : input.outstanding;

  const blocks: HtmlValue[] = [];
  const textLines: string[] = [];

  blocks.push(paragraph('Team —'));
  textLines.push('Team —', '');

  blocks.push(paragraph(html`Loan documents have been ordered for <b>Loan ${input.loanNumber}</b> (${input.borrowerName}). The closing team will place the file in line for review and will confirm all fees and dates prior to the release of loan documents.`));
  textLines.push(
    `Loan documents have been ordered for Loan ${input.loanNumber} (${input.borrowerName}). The closing team will place the file in line for review and will confirm all fees and dates prior to the release of loan documents.`,
    ''
  );

  blocks.push(paragraph(html`Docs requested by <b>${input.docsRequestedBy}</b>.`));
  textLines.push(`Docs requested by ${input.docsRequestedBy}.`, '');

  if (input.sequence > 1 && input.resubmittedAt) {
    blocks.push(paragraph(html`<i>Re-submitted on ${input.resubmittedAt}.</i>`));
    textLines.push(`Re-submitted on ${input.resubmittedAt}.`, '');
  }

  if (input.deferred) {
    const reason = input.deferReason || 'No reason provided — please follow up with the processor.';
    blocks.push(
      paragraph(html`<b>Insurance was deferred at submission — EOI has not yet been received.</b>`),
      callout({ tone: 'attention', body: reason })
    );
    textLines.push('⚠️  Insurance was deferred at submission — EOI has not yet been received.', '', `Reason provided by the processor:`, `> ${reason}`, '');

    const snapRows = input.hoaCoversWallsIn
      ? [{ label: 'HOA Master Policy', value: 'Covers walls-in with betterments and improvements — no separate hazard policy required.' }]
      : (input.insuranceSnapshot ?? []);
    const dicRows = !input.hoaCoversWallsIn && input.dicSnapshot && input.dicSnapshot.length ? input.dicSnapshot : [];
    blocks.push(
      panel(html`<p style="margin:0 0 8px;font-weight:bold;color:${BRAND.ink};">Section 4 — Insurance (snapshot at submission)</p>${snapshotTable(snapRows)}${dicRows.length ? html`<p style="margin:10px 0 4px;font-weight:bold;color:${STATUS_TONES.attention.text};">California Fair Plan — DIC (Differences in Conditions) policy required</p>${snapshotTable(dicRows)}` : ''}`),
      paragraph(html`<b>Closing cannot balance or release docs until the EOI has been received.</b>`)
    );
    textLines.push('Section 4 — Insurance (snapshot at submission)', '─────────────────────────────────────────────');
    for (const r of snapRows) textLines.push(`${r.label}:  ${r.value || '—'}`);
    if (!input.hoaCoversWallsIn && input.dicSnapshot?.length) {
      textLines.push('California Fair Plan — DIC policy required');
      for (const r of input.dicSnapshot) textLines.push(`${r.label}:  ${r.value || '—'}`);
    }
    textLines.push('', 'Closing cannot balance or release docs until the EOI has been received.', '');
  }

  if (visibleOutstanding.length > 0) {
    // Each item leads with its tag as a small pill, then the reason under it.
    const pill = (tag: string) =>
      html`<span style="display:inline-block;padding:1px 8px;border-radius:999px;background:${BRAND.cream};border:1px solid ${BRAND.badgeBorder};color:${STATUS_TONES.attention.text};font-size:12px;line-height:16px;font-weight:bold;vertical-align:middle;margin-right:6px;">${tag}</span>`;
    const items = visibleOutstanding.map((o) =>
      html`${pill(o.tag)}${o.label}${o.reason ? html`<br><span style="color:${STATUS_TONES.attention.text};font-size:13px;">Reason: ${o.reason}</span>` : ''}`
    );
    blocks.push(callout({ tone: 'attention', title: 'Outstanding before docs can be drawn', body: taggedList(items) }));
    textLines.push('Outstanding before docs can be drawn:');
    for (const o of visibleOutstanding) {
      textLines.push(`  - [${o.tag}] ${o.label}${o.reason ? ` (Reason: ${o.reason})` : ''}`);
    }
    textLines.push('');
  } else if (!input.deferred) {
    blocks.push(callout({ tone: 'success', body: html`<b>Order is complete — no outstanding items.</b>` }));
    textLines.push('Order is complete — no outstanding items.', '');
  }

  const closing = `A copy of the completed doc order is attached to this email. Please reply all to this email with any changes. Last-minute updates may delay the release of loan documents, so please confirm all details are accurate and communicate any changes as soon as possible.`;
  blocks.push(paragraph(closing));
  textLines.push(closing, '');

  // Chip: attention while insurance is deferred or items are outstanding; otherwise the order is complete.
  const status: StatusChip = input.deferred
    ? { tone: 'attention', label: 'Insurance deferred' }
    : visibleOutstanding.length > 0
      ? { tone: 'attention', label: `${visibleOutstanding.length} outstanding` }
      : { tone: 'info', label: 'Complete' };

  return {
    subject,
    html: docOrderEmail({
      title: 'Doc order submitted',
      status,
      loanNumber: input.loanNumber,
      borrowerName: input.borrowerName,
      blocks,
      signName: input.processorName,
      signOrg: 'Lendwise Doc Order',
    }),
    text: [...textLines, `— ${input.processorName}`, 'Lendwise Doc Order'].join('\n'),
  };
}

export interface E2Input {
  loanNumber: string;
  borrowerName: string;
  closerName: string;
  loFirstName: string;
  portalUrl: string; // production deep link
  pdfFilename: string;
}

/** E2 — Doc Order Ready to Review & Sign (to the LO, PDF attached, deep link). */
export function buildE2SentToLo(input: E2Input): EmailContent {
  const subject = headerText(`Doc Order Ready to Review & Sign — Loan ${input.loanNumber} — ${input.borrowerName}`.trim());
  // The title must not repeat the subject: the Workspace preview shows both.
  const emailHtml = docOrderEmail({
    title: 'Ready for your review and signature',
    status: { tone: 'attention', label: 'Signature needed' },
    loanNumber: input.loanNumber,
    borrowerName: input.borrowerName,
    blocks: [
      paragraph(`${input.loFirstName} —`),
      paragraph(html`The closing team has reviewed and finalized the doc order for <b>Loan ${input.loanNumber}</b> (${input.borrowerName}). All third-party fees and closing details have been confirmed and are ready for your final review and signature.`),
      attachmentChip(input.pdfFilename, 'PDF · finalized doc order (attached)'),
      paragraph('Open the order in the Lendwise portal to review the final figures and sign — the link takes you straight to your view of this doc order:'),
      button({ href: normalizeWebUrl(`https://${input.portalUrl}`), label: 'Review & sign in the Lendwise portal', align: 'left' }),
      note(input.portalUrl),
      paragraph('Once you sign, the order is complete and your compensation locks to the figures shown. Reply all with any questions before signing.'),
    ],
    signName: input.closerName,
    signOrg: 'Lendwise Closing',
  });
  const text = [
    `${input.loFirstName} —`,
    '',
    `The closing team has reviewed and finalized the doc order for Loan ${input.loanNumber} (${input.borrowerName}). All third-party fees and closing details have been confirmed and are ready for your final review and signature.`,
    '',
    `Attached: ${input.pdfFilename} (PDF — finalized doc order)`,
    '',
    `Review & sign in the Lendwise portal: https://${input.portalUrl}`,
    '',
    `Once you sign, the order is complete and your compensation locks to the figures shown. Reply all with any questions before signing.`,
    '',
    `— ${input.closerName}`,
    'Lendwise Closing',
  ].join('\n');
  return { subject, html: emailHtml, text };
}

export interface E3Input {
  loanNumber: string;
  borrowerName: string;
  loanOfficerName: string;
  requestedAmount: string; // formatted money
  availableCapacity: string; // formatted money — P&P generic lender-credit capacity
}

/** E3 — Lender Credit Escalation to the lock desk (A4 approved copy). */
export function buildE3LockDeskEscalation(input: E3Input): EmailContent {
  const subject = headerText(`Lender Credit Escalation — Loan ${input.loanNumber} — ${input.borrowerName}`.trim());
  const body = `${input.loanOfficerName} has requested a lender credit of ${input.requestedAmount} on Loan ${input.loanNumber} (${input.borrowerName}) that exceeds the available pool of ${input.availableCapacity}. Please review and advise whether the additional credit can be approved.`;
  const emailHtml = docOrderEmail({
    title: 'Lender credit escalation',
    status: { tone: 'attention', label: 'Review requested' },
    loanNumber: input.loanNumber,
    borrowerName: input.borrowerName,
    blocks: [
      paragraph('Lock desk —'),
      paragraph(html`${input.loanOfficerName} has requested a lender credit of <b>${input.requestedAmount}</b> on Loan ${input.loanNumber} (${input.borrowerName}) that exceeds the available pool of ${input.availableCapacity}. Please review and advise whether the additional credit can be approved.`),
    ],
    signName: 'Lendwise Doc Order',
    signOrg: 'lendwisemtg.com',
  });
  return { subject, html: emailHtml, text: ['Lock desk —', '', body, '', '— Lendwise Doc Order'].join('\n') };
}

export interface E4FeeDelta {
  name: string;
  fromAmount: string;
  toAmount: string;
  amtChanged: boolean;
  fromReimburse: boolean;
  toReimburse: boolean;
  reimbChanged: boolean;
}

export interface E4Input {
  loanNumber: string;
  borrowerName: string;
  loanOfficerName: string;
  note: string;
  deltas: E4FeeDelta[];
}

/** E4 — LO requests changes (to closing@; A4 approved copy). */
export function buildE4ChangesRequested(input: E4Input): EmailContent {
  const subject = headerText(`Changes Requested — Loan ${input.loanNumber} — ${input.borrowerName}`.trim());
  const deltaLine = (d: E4FeeDelta): string => {
    const parts: string[] = [];
    if (d.amtChanged) parts.push(`${d.fromAmount} → ${d.toAmount}`);
    if (d.reimbChanged) parts.push(`reimbursement ${d.fromReimburse ? 'on' : 'off'} → ${d.toReimburse ? 'on' : 'off'}`);
    return `${d.name}: ${parts.join(' · ')}`;
  };
  const emailHtml = docOrderEmail({
    title: 'Changes requested',
    status: { tone: 'attention', label: 'Returned to closing' },
    loanNumber: input.loanNumber,
    borrowerName: input.borrowerName,
    blocks: [
      paragraph('Closing —'),
      paragraph(html`${input.loanOfficerName} has reviewed the doc order for Loan ${input.loanNumber} (${input.borrowerName}) and is requesting changes before acknowledging:`),
      callout({ tone: 'info', body: input.note }),
      paragraph(html`<b>Requested fee changes:</b>`),
      input.deltas.length
        ? bulletList(input.deltas.map((d) => deltaLine(d)))
        : paragraph(html`<i>No specific fee changes — see the note above.</i>`),
      paragraph('The order has been returned to Closing for revision.'),
    ],
    signName: 'Lendwise Doc Order',
    signOrg: 'lendwisemtg.com',
  });
  const text = [
    'Closing —',
    '',
    `${input.loanOfficerName} has reviewed the doc order for Loan ${input.loanNumber} (${input.borrowerName}) and is requesting changes before acknowledging:`,
    '',
    input.note,
    '',
    'Requested fee changes:',
    ...(input.deltas.length ? input.deltas.map((d) => `  - ${deltaLine(d)}`) : ['  (none — see note)']),
    '',
    'The order has been returned to Closing for revision.',
    '',
    '— Lendwise Doc Order',
  ].join('\n');
  return { subject, html: emailHtml, text };
}

export interface E5Input {
  loanNumber: string;
  borrowerName: string;
  loanOfficerName: string;
  signedAt: string;
  pdfFilename: string;
}

/** E5 — LO signed Doc Order (sent only after the 4794 write is validated). */
export function buildE5LoSigned(input: E5Input): EmailContent {
  const subject = headerText(`Loan Officer Signed Doc Order — Loan ${input.loanNumber} — ${input.borrowerName}`.trim());
  const emailHtml = docOrderEmail({
    title: 'Doc order signed',
    status: { tone: 'success', label: 'Signed' },
    loanNumber: input.loanNumber,
    borrowerName: input.borrowerName,
    blocks: [
      paragraph('Team —'),
      paragraph(html`<b>${input.loanOfficerName}</b> reviewed and signed the Doc Order for <b>Loan ${input.loanNumber}</b> (${input.borrowerName}) on ${input.signedAt}.`),
      paragraph('The Doc Order is complete. Any lender-credit update has been validated in Encompass.'),
      attachmentChip(input.pdfFilename, 'PDF · signed loan officer doc order (attached)'),
      paragraph('The attached LO version is the signed record for the completed order.'),
    ],
    signName: 'Lendwise Doc Order',
    signOrg: 'lendwisemtg.com',
  });
  const text = [
    'Team —',
    '',
    `${input.loanOfficerName} reviewed and signed the Doc Order for Loan ${input.loanNumber} (${input.borrowerName}) on ${input.signedAt}.`,
    '',
    'The Doc Order is complete. Any lender-credit update has been validated in Encompass.',
    '',
    `Attached: ${input.pdfFilename} (PDF — signed loan officer doc order)`,
    '',
    'The attached LO version is the signed record for the completed order.',
    '',
    '— Lendwise Doc Order'
  ].join('\n');
  return { subject, html: emailHtml, text };
}
